import {OptionConfig, OptionModel} from 'models/configuration/OptionModel'
import {PackConfig, PackModel} from 'models/configuration/PackModel'
import {TourConfig, TourModel} from 'models/configuration/TourModel'
import {TrackConfig, TrackModel} from 'models/configuration/TrackModel'
import {ProgramData, ProgramModel} from 'models/program/ProgramModel'
import {ImageOrSvgType, ImageType, UrlType} from 'types'
import {Memoize} from 'util/memoize'
import {FooterType} from 'views/layout/footer'
import {HeaderType} from 'views/layout/header'

export type EventState = {
	id: number
	url: UrlType
	title: string
	order: number
	status: 'pre' | 'registration' | 'ongoing' | 'after'
	theme: 'futuresummits' | 'itf'
	date: string
	background: ImageType
	logos: {
		light: ImageOrSvgType
		light_powered: ImageOrSvgType
		colored: ImageOrSvgType
		colored_powered: ImageOrSvgType
	}
	events: Array<TrackConfig>
	packs: Array<PackConfig>
	options: Array<OptionConfig>
	tours: Array<TourConfig>
	no_show: boolean

	// Optionally filled
	header?: HeaderType
	footer?: FooterType

	// TODO: for some pages this should better be fetched by ajax request when needed
	program?: ProgramData
}

const fileOrImagePath: (ImageOrSvgType) => string = input =>
	input && (input.src || input.url)

export class EventModel {
	state: EventState

	constructor(state: EventState) {
		this.state = state
	}

	@Memoize()
	get tracks(): Array<TrackModel> {
		return this.state.events.map(data => new TrackModel(this, data))
	}

	@Memoize()
	get packs(): Array<PackModel> {
		return this.state.packs.map(data => new PackModel(this, data))
	}

	@Memoize()
	get options(): Array<OptionModel> {
		return this.state.options.map(data => new OptionModel(this, data))
	}

	@Memoize()
	get tours(): Array<TourModel> {
		return this.state.tours.map(data => new TourModel(this, data))
	}

	@Memoize()
	get sharedOptions(): Array<OptionModel> {
		return this.options.filter(option => option.tracks.length > 1)
	}

	@Memoize()
	get program(): ProgramModel {
		return new ProgramModel(this.state.program)
	}

	@Memoize()
	get isDarkTheme(): boolean {
		return this.state.theme === 'itf'
	}

	@Memoize()
	get id(): number {
		return this.state.id
	}

	@Memoize()
	get title(): string {
		return this.state.title
	}

	@Memoize()
	get year(): number {
		const urlPart = this.state.url.split('/')[1]
		return parseInt(urlPart, 10) || 2023
	}

	@Memoize()
	get adminTitle(): string {
		return this.year + ' | ' + this.state.title
	}

	@Memoize()
	get url(): UrlType {
		return this.state.url
	}

	@Memoize()
	get background(): ImageType {
		return this.state.background
	}

	@Memoize()
	get header(): HeaderType {
		return this.state.header
	}

	@Memoize()
	get footer(): FooterType {
		return this.state.footer
	}

	getLogoSrc(white: boolean, powered: boolean): string {
		return fileOrImagePath(this.getLogoFile(white, powered))
	}

	getLogoFile(white: boolean, powered: boolean): ImageOrSvgType {
		const logos = this.state.logos
		if (white && powered) return logos.light_powered || logos.light
		if (white) return logos.light
		if (powered) return logos.colored_powered || logos.colored
		return logos.colored
	}

	getEloquaDataFor(
		packs: Array<string>,
		options: Record<string, string>,
		eloquaDetails: Record<string, string>
	) {
		const {
			postal_code,
			other_invoice_address,
			invoice_address,
			invoice_address_line2,
			invoice_postal_code,
			invoice_city,
			invoice_state,
			invoice_country,
			invoice_vat,
			eloqua,
			...rest
		} = eloquaDetails
		const allTracks = this.tracks.reduce(
			(acc, track) => ({
				...acc,
				[track.key]: track.isIncludedIn(new Set(packs)) ? 'True' : 'False'
			}),
			{}
		)
		const allPacks = this.packs.reduce(
			(acc, pack) => ({
				...acc,
				[`pack_${pack.key}`]: packs.includes(pack.key) ? 'True' : 'False'
			}),
			{}
		)
		const skippedOptions = this.options
			.filter(o =>
				packs?.every(pack => o.skippedPackaged.map(p => p.key).includes(pack))
			)
			.map(o => o.key)
		const allOptions = this.options.reduce(
			(acc, curr) => ({
				...acc,
				[curr.key]: skippedOptions.includes(curr.key) ? '' : options[curr.key]
			}),
			{}
		)
		return {
			eloqua: {
				...rest,
				zipcode: postal_code,
				...allTracks,
				...allOptions,
				...allPacks
			}
		}
	}

	getLGDTagsFor(
		packs: Array<string>,
		options: Record<string, string>
	): Record<string, boolean> {
		const packTags = this.packs
			.filter(pack => packs.includes(pack.key))
			.reduce((acc, curr) => [...acc, ...curr.tagsSet], [])
		const optionTags = Object.entries(options).map(
			([key, value]) =>
				this.options
					.find(option => option.key === key)
					?.choices.find(c => c.key === value)?.tag
		)
		const tags = [...new Set(packTags), ...optionTags]
			.filter(tag => !!tag)
			.reduce((acc, curr) => ({...acc, [curr]: true}), {})

		return tags
	}
}
